/**
 * Klaviyo status-message accessibility (WCAG 4.1.3 Status Messages) — JLKA-E120 / ADA-442.
 *
 * Klaviyo-hosted signup forms (popups, flyouts, and in-page embeds — the markup Klaviyo injects
 * with `kl-private-reset-css-*` classes) surface validation errors, submit errors, and success
 * confirmations by swapping text into the form. That text is not inside an ARIA live region, so
 * screen readers never announce it. We don't control Klaviyo's markup and can't add roles to it,
 * and Klaviyo's `klaviyoForms` events don't carry the message text — so this is a theme-side
 * workaround:
 *
 *   1. Two live regions the theme owns are added to the DOM, empty, at load (SRs often miss live
 *      regions injected later, and only announce changes that happen *after* the region exists).
 *   2. On any DOM change inside a Klaviyo form we RE-SCAN its current message text and mirror any
 *      newly-appeared message into the matching region — polite `role="status"` for success/info,
 *      assertive `role="alert"` for errors (and for anything that lands right after a submit).
 *
 * Why re-scan instead of reading MutationObserver records? Klaviyo renders an error/success by
 * replacing the whole form subtree, so the message text arrives *bundled inside a freshly-added
 * container*. A MutationObserver never replays content added together with its container, so reading
 * records misses it. Diffing the current DOM against the previous scan catches it regardless of how
 * Klaviyo re-mounts. Mirroring the text (rather than retro-fitting roles onto Klaviyo's nodes) also
 * keeps us resilient to Klaviyo's hashed, version-specific class names.
 */
(function () {
  'use strict';

  var KL_PREFIX = 'kl-private-reset';
  var MAX_LEN = 400; // longer blocks are form/step scaffolding, not a status message
  var DEDUPE_MS = 3000; // ignore the same text re-announced within this window (React re-renders)
  var SEED_WINDOW_MS = 1500; // text present within this window of first sighting is the form's initial
                             // content (headings, descriptions) — seeded, not announced
  var SUBMIT_WINDOW_MS = 6000; // messages appearing this soon after a submit are treated as errors (assertive)
  var LIVE_REGION_CLASS = 'klaviyo-a11y-live-region';

  var statusRegion = null;
  var alertRegion = null;
  var recent = {}; // normalized text -> last-announced timestamp
  var seededTexts = {}; // the form's initial chrome (headings/descriptions) — never announced
  var previousTexts = {}; // message text present at the previous scan
  var firstSeenAt = 0; // timestamp Klaviyo content was first detected
  var lastSubmitAt = 0; // timestamp of the last Enter/submit-click inside a Klaviyo form
  var scanScheduled = false;

  /* ---------- live regions ---------- */

  function makeRegion(role, politeness) {
    var el = document.createElement('div');
    // `no-empty` + inline display exempt the empty region from the global `:empty { display:none }`
    // rule (theme.css) — a display:none live region is never announced.
    el.className = 'visually-hidden no-empty ' + LIVE_REGION_CLASS;
    el.setAttribute('role', role);
    el.setAttribute('aria-live', politeness);
    el.setAttribute('aria-atomic', 'true');
    el.style.display = 'block';
    return el;
  }

  function ensureRegions() {
    if (statusRegion && alertRegion) return;
    statusRegion = makeRegion('status', 'polite');
    alertRegion = makeRegion('alert', 'assertive');
    document.body.appendChild(statusRegion);
    document.body.appendChild(alertRegion);
  }

  // Clear then set on a later tick so the two are distinct mutations the screen reader registers
  // (setTimeout, not requestAnimationFrame, which is throttled in background tabs). Mirrors facets.js.
  function setRegion(region, text) {
    region.textContent = '';
    setTimeout(function () {
      region.textContent = text;
    }, 60);
  }

  // Move focus to the message element itself. VoiceOver reliably reads a focused element, whereas it
  // drops live-region updates that land after the submit drops focus to <body> ("…web content"). The
  // element may have been re-rendered since detection, so we re-find it by its text at focus time.
  function focusMessage(text) {
    var roots = topLevelKlaviyoRoots();
    var found = [];
    for (var r = 0; r < roots.length; r++) collectMessages(roots[r], found);
    for (var i = 0; i < found.length; i++) {
      var el = found[i].el;
      if (found[i].text === text && el.isConnected) {
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        el.style.outline = 'none'; // non-interactive message — don't show a focus ring on it
        // preventScroll keeps the viewport still; the message is already visible in the form, so
        // scrolling to it just reads as the page "jumping around".
        try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
        return;
      }
    }
  }

  function announce(text, isError) {
    ensureRegions();
    var region = isError ? alertRegion : statusRegion;
    setRegion(region, text);
    if (isError) {
      // Delay past the browser's/Klaviyo's own focus-to-<body> on submit so our focus isn't stolen
      // back immediately; then move focus to the message so VoiceOver announces it.
      setTimeout(function () { focusMessage(text); }, 450);
    }
  }

  /* ---------- Klaviyo node detection ---------- */

  function isKlaviyoEl(el) {
    if (!el || !el.classList) return false;
    for (var i = 0; i < el.classList.length; i++) {
      if (el.classList[i].indexOf(KL_PREFIX) === 0) return true;
    }
    return false;
  }

  function hasKlaviyoAncestor(el) {
    var node = el;
    while (node) {
      if (isKlaviyoEl(node)) return true;
      node = node.parentElement;
    }
    return false;
  }

  // Top-level Klaviyo roots: matching elements that aren't nested inside another Klaviyo element.
  function topLevelKlaviyoRoots() {
    var matches = document.querySelectorAll('[class*="' + KL_PREFIX + '"]');
    var roots = [];
    for (var i = 0; i < matches.length; i++) {
      if (!hasKlaviyoAncestor(matches[i].parentElement)) roots.push(matches[i]);
    }
    return roots;
  }

  /* ---------- message extraction ---------- */

  var SKIP_TAGS = { input: 1, select: 1, textarea: 1, button: 1, label: 1, a: 1, script: 1, style: 1, svg: 1 };

  function hasFormControl(el) {
    return !!(el.querySelector && el.querySelector('input, select, textarea, button, [role="button"]'));
  }

  // Collect control-free text blocks (the shape of a status message) from a Klaviyo subtree.
  // A block that still contains form controls is form/step scaffolding, so we descend past it to
  // find the message sub-block rather than announcing the whole form.
  function collectMessages(node, out) {
    if (!node || node.nodeType !== 1) return;
    var tag = node.tagName.toLowerCase();
    if (tag === 'form') {
      for (var c = 0; c < node.children.length; c++) collectMessages(node.children[c], out);
      return;
    }
    if (SKIP_TAGS[tag]) return;

    if (!hasFormControl(node)) {
      var text = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (text && text.length <= MAX_LEN) out.push({ el: node, text: text });
      return;
    }
    for (var i = 0; i < node.children.length; i++) collectMessages(node.children[i], out);
  }

  function looksLikeError(el, text) {
    var node = el;
    for (var i = 0; i < 4 && node; i++) {
      if (node.getAttribute && node.getAttribute('role') === 'alert') return true;
      var cls = node.className && node.className.toString ? node.className.toString().toLowerCase() : '';
      if (cls && /error|invalid|danger/.test(cls)) return true;
      node = node.parentElement;
    }
    return /\b(error|invalid|required|must|valid email|try again|problem|unable|failed|wrong|already)\b/i.test(text);
  }

  function recentlyAnnounced(text) {
    var now = Date.now();
    for (var k in recent) {
      if (recent.hasOwnProperty(k) && now - recent[k] > DEDUPE_MS) delete recent[k];
    }
    if (recent.hasOwnProperty(text)) return true;
    recent[text] = now;
    return false;
  }

  /* ---------- scan / announce ---------- */

  // Re-read every Klaviyo root's current message text and announce whatever is new since the last
  // scan. Deepest leaf wins so we keep the most specific element (best for error-vs-status detection).
  function scan() {
    scanScheduled = false;
    var roots = topLevelKlaviyoRoots();
    if (!roots.length) return;

    var found = [];
    for (var r = 0; r < roots.length; r++) collectMessages(roots[r], found);

    var now = Date.now();
    if (firstSeenAt === 0 && found.length) firstSeenAt = now;
    var withinSeedWindow = now - firstSeenAt < SEED_WINDOW_MS;
    var recentlySubmitted = now - lastSubmitAt < SUBMIT_WINDOW_MS;

    var current = {};
    for (var i = 0; i < found.length; i++) {
      var text = found[i].text;
      if (current[text]) continue; // de-dupe within this scan
      current[text] = true;

      if (previousTexts[text]) continue; // was already present — not a new message

      var errorish = looksLikeError(found[i].el, text);
      // During the seed window the form's own initial content (headings/descriptions) is remembered
      // permanently so it's never announced — even if Klaviyo later reverts to the pristine form. An
      // actual error or anything following a submit is still announced, never seeded.
      if (withinSeedWindow && !errorish && !recentlySubmitted) {
        seededTexts[text] = true;
        continue;
      }
      if (seededTexts[text]) continue; // initial chrome reappearing — not a status message
      if (recentlyAnnounced(text)) continue;
      announce(text, errorish || recentlySubmitted);
    }
    previousTexts = current;
  }

  function scheduleScan() {
    if (scanScheduled) return;
    scanScheduled = true;
    setTimeout(scan, 100);
  }

  /* ---------- submit detection ---------- */

  function markSubmit(target) {
    if (hasKlaviyoAncestor(target)) lastSubmitAt = Date.now();
  }

  function init() {
    if (typeof MutationObserver !== 'function') return;
    ensureRegions();
    scheduleScan(); // seed from any form already mounted

    // Detect a submit attempt inside a Klaviyo form (native submit, Enter in a field, or a click on
    // the submit button) so messages that follow are announced assertively — on submit the form
    // re-renders, focus drops to <body>, and VoiceOver announces the page ("…web content"); a polite
    // message loses that race and is dropped, so a post-submit message must interrupt. Captured on the
    // document because Klaviyo handles submit in JS and may not fire a native form `submit` event.
    document.addEventListener('submit', function (e) { markSubmit(e.target); }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') markSubmit(e.target);
    }, true);
    document.addEventListener('click', function (e) {
      var el = e.target;
      while (el && el.nodeType === 1) {
        var tag = el.tagName.toLowerCase();
        if (tag === 'button' || el.getAttribute('role') === 'button' || el.getAttribute('type') === 'submit') {
          markSubmit(el);
          break;
        }
        el = el.parentElement;
      }
    }, true);

    // Any DOM change (Klaviyo mounting, re-rendering, swapping in an error/success view) triggers a
    // debounced re-scan. Typing in inputs does not mutate the DOM, so this stays quiet until Klaviyo
    // actually changes the page.
    var observer = new MutationObserver(scheduleScan);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
